import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Car,
  Home,
  MapPin,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  X,
  ShoppingCart,
  AlertCircle,
} from 'lucide-react';
import { useGetUserMarketplaceListingQuery, useCreateMarketplaceInquiryMutation } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import axios from 'axios';
import Navbar from '../../components/common/Navbar';

const MarketplaceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const listingId = id ? parseInt(id) : null;
  const isValidId = listingId && !isNaN(listingId) && listingId > 0;

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [inquiryPhone, setInquiryPhone] = useState('');
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);

  const { data: listingResponse, isLoading } = useGetUserMarketplaceListingQuery(listingId!, {
    skip: !isValidId,
    refetchOnMountOrArgChange: true,
  });
  const [createInquiry] = useCreateMarketplaceInquiryMutation();

  const listing = listingResponse?.data;

  const formatCurrency = (amount: number, symbol: string | null = null) => {
    const currencySymbol = symbol || '$';
    return `${currencySymbol}${amount.toLocaleString('en-US')}`;
  };

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to send an inquiry');
      if (window.innerWidth < 1024) {
        window.location.href = "https://app.diasporawise.com";
      } else {
        navigate('/');
      }
      return;
    }

    if (!inquiryMessage.trim()) {
      toast.error('Please enter your message');
      return;
    }

    setIsSubmittingInquiry(true);
    try {
      const result = await createInquiry({
        listing_id: listingId!,
        message: inquiryMessage,
        contact_phone: inquiryPhone || undefined,
      }).unwrap();

      if (result?.success) {
        toast.success('Inquiry sent successfully! Admin will contact you soon.');
        setShowInquiryModal(false);
        setInquiryMessage('');
        setInquiryPhone('');
      } else {
        toast.error(result?.message || 'Failed to send inquiry');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to send inquiry. Please try again.');
      console.error('Inquiry error:', error);
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  const handleMakePayment = () => {
    if (!user) {
      toast.error('Please login to make a payment');
      if (window.innerWidth < 1024) {
        window.location.href = 'https://app.diasporawise.com';
      } else {
        navigate('/');
      }
      return;
    }
    navigate(`/marketplace/${listingId}/payment`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Card className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Listing Not Found</h2>
            <p className="text-gray-600 mb-6">The listing you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
          </Card>
        </div>
      </div>
    );
  }

  const images = listing.images || [];
  const primaryImage = images.find((img: any) => img.is_primary) || images[0] || null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/marketplace')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Marketplace</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden bg-white p-0">
              {images.length > 0 ? (
                <div className="relative bg-white">
                  <div className="w-full h-96 bg-white flex items-center justify-center overflow-hidden">
                    <img
                      src={primaryImage?.image_path || images[selectedImageIndex]?.image_path}
                      alt={listing.title}
                      className="w-full h-full object-cover cursor-pointer bg-white"
                      onClick={() => setShowImageModal(true)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600/FFFFFF/CCCCCC?text=No+Image';
                      }}
                    />
                  </div>
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length)}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setSelectedImageIndex((selectedImageIndex + 1) % images.length)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                        {images.map((_: any, idx: number) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              selectedImageIndex === idx ? 'bg-white w-8' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full h-96 bg-white flex items-center justify-center border border-gray-200">
                  <span className="text-gray-400">No Image Available</span>
                </div>
              )}

              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex gap-2 overflow-x-auto">
                    {images.map((imageItem: any, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all bg-white ${
                          selectedImageIndex === idx ? 'border-gray-800' : 'border-gray-200'
                        }`}
                      >
                        <img
                          src={imageItem.image_path}
                          alt={`${listing.title} ${idx + 1}`}
                          className="w-full h-full object-cover bg-white"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150/FFFFFF/CCCCCC?text=No+Image';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Details */}
            <Card className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {listing.listing_type === 'car' ? (
                      <Car className="w-6 h-6 text-gray-600" />
                    ) : (
                      <Home className="w-6 h-6 text-gray-600" />
                    )}
                    <h1 className="text-3xl font-bold text-gray-900">{listing.title}</h1>
                  </div>
                  {listing.location && (
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{listing.location}</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {formatCurrency(listing.price, listing.currency_symbol)}
                  </div>
                  {listing.is_featured && (
                    <Badge variant="secondary" className="bg-yellow-500 text-white">
                      Featured
                    </Badge>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 whitespace-pre-line">{listing.description}</p>
              </div>

              {/* Car/House Details */}
              {(listing.car_details || listing.house_details) && (
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {listing.listing_type === 'car' ? 'Car Details' : 'House Details'}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {listing.listing_type === 'car' && listing.car_details && (
                      <>
                        {listing.car_details.make && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Make</label>
                            <p className="text-gray-900">{listing.car_details.make}</p>
                          </div>
                        )}
                        {listing.car_details.model && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Model</label>
                            <p className="text-gray-900">{listing.car_details.model}</p>
                          </div>
                        )}
                        {listing.car_details.year && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Year</label>
                            <p className="text-gray-900">{listing.car_details.year}</p>
                          </div>
                        )}
                        {listing.car_details.mileage && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Mileage</label>
                            <p className="text-gray-900">{listing.car_details.mileage}</p>
                          </div>
                        )}
                        {listing.car_details.color && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Color</label>
                            <p className="text-gray-900">{listing.car_details.color}</p>
                          </div>
                        )}
                        {listing.car_details.fuel_type && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Fuel Type</label>
                            <p className="text-gray-900">{listing.car_details.fuel_type}</p>
                          </div>
                        )}
                      </>
                    )}
                    {listing.listing_type === 'house' && listing.house_details && (
                      <>
                        {listing.house_details.bedrooms && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Bedrooms</label>
                            <p className="text-gray-900">{listing.house_details.bedrooms}</p>
                          </div>
                        )}
                        {listing.house_details.bathrooms && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Bathrooms</label>
                            <p className="text-gray-900">{listing.house_details.bathrooms}</p>
                          </div>
                        )}
                        {listing.house_details.square_feet && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Square Feet</label>
                            <p className="text-gray-900">{listing.house_details.square_feet}</p>
                          </div>
                        )}
                        {listing.house_details.property_type && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Property Type</label>
                            <p className="text-gray-900">{listing.house_details.property_type}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Action Buttons */}
            <Card className="p-6">
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setShowInquiryModal(true)}
                  className="w-full bg-white border-2 border-gray-800 text-gray-900 hover:bg-gray-50 hover:border-gray-800 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  Send Inquiry
                </button>
                <Button
                  onClick={handleMakePayment}
                  className="w-full bg-gray-800 text-white hover:bg-gray-900 flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Make Payment
                </Button>
              </div>
            </Card>

            {/* Quick Info */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Price</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(listing.price, listing.currency_symbol)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type</span>
                  <Badge variant="secondary">
                    {listing.listing_type === 'car' ? 'Car' : 'House'}
                  </Badge>
                </div>
                {listing.location && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location</span>
                    <span className="text-gray-900">{listing.location}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
      </div>

      {/* Image Modal */}
      {showImageModal && images.length > 0 && (
        <Modal isOpen={showImageModal} onClose={() => setShowImageModal(false)} title="" size="xl">
          <div className="relative bg-white">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={images[selectedImageIndex]?.image_path}
              alt={listing.title}
              className="w-full h-auto max-h-[80vh] object-contain bg-white"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600/FFFFFF/CCCCCC?text=No+Image';
              }}
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setSelectedImageIndex((selectedImageIndex + 1) % images.length)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Inquiry Modal */}
      <Modal isOpen={showInquiryModal} onClose={() => setShowInquiryModal(false)} title="Send Inquiry">
        <form onSubmit={handleInquirySubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Message *
              </label>
              <textarea
                value={inquiryMessage}
                onChange={(e) => setInquiryMessage(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Ask questions about this listing..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                value={inquiryPhone}
                onChange={(e) => setInquiryPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Your contact number"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowInquiryModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingInquiry} className="flex-1 bg-gray-800">
                {isSubmittingInquiry ? 'Sending...' : 'Send Inquiry'}
              </Button>
            </div>
          </form>
      </Modal>
    </>
  );
};

export default MarketplaceDetail;
